// src/components/CategoryChips.tsx

import { useRef } from "react";
import styled from "styled-components";
import { FiX, FiChevronLeft, FiChevronRight } from "react-icons/fi";

const Wrapper = styled.div`
  position: relative;
  display: block;
  width: 100%;
  overflow: hidden;

  max-width: 100%;
`;

const ArrowButton = styled.button<{ left?: boolean }>`
  position: absolute;
  top: 50%;
  ${({ left }) => (left ? "left: 0" : "right: 0")};
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.8);
  border: none;
  border-radius: 50%;
  padding: 4px;
  cursor: pointer;
  z-index: 1;

  &:hover {
    background: rgba(255, 255, 255, 1);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const ChipContainer = styled.div`
  display: flex;
  gap: 4px;
  padding: 2px 24px; /* space so chips don’t butt under arrows */
  white-space: nowrap;
  cursor: grab;
  user-select: none;

  width: 100%; /* fill the wrapper */
  overflow-x: auto; /* enable horizontal scroll */
  -webkit-overflow-scrolling: touch; /* smooth on iOS */

  &::-webkit-scrollbar {
    display: none;
  }
  &:active {
    cursor: grabbing;
  }

  @media (max-width: 768px) {
    padding: 2px 4px;
  }
`;

const Chip = styled.button<{ selected: boolean }>`
  background-color: ${({ selected }) =>
    selected ? "rgb(37, 99, 235)" : "#dfe4ff"};
  color: ${({ selected }) => (selected ? "white" : "#4a5568")};
  padding: 6px 10px;
  border-radius: 14px;
  border: none;
  cursor: pointer;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 4px;
  line-height: 1;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #5a86e6;
  }
`;

const CloseIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 0;

  svg {
    display: block;
  }
`;

type Props = {
  categories: string[];
  selectedCategory: string;
  onCategoryChange: (category: string) => void;
  leadingChips?: Array<{
    key: string;
    label: string;
    selected: boolean;
    onClick: () => void;
  }>;
};

const CategoryChips = ({
  categories,
  selectedCategory,
  onCategoryChange,
  leadingChips = [],
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const filtered = categories.filter((c) => c !== "All");

  const scrollBy = (distance: number) => {
    containerRef.current?.scrollBy({ left: distance, behavior: "smooth" });
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    if (containerRef.current) {
      containerRef.current.scrollLeft += (e.deltaY || e.deltaX) * 2;
    }
  };

  const handleDrag = (e: React.MouseEvent) => {
    if (!containerRef.current) return;
    const c = containerRef.current;
    let startX = e.clientX;
    let scrollLeft = c.scrollLeft;

    const onMouseMove = (ev: MouseEvent) => {
      c.scrollLeft = scrollLeft - (ev.clientX - startX) * 2;
    };
    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  const toggle = (cat: string) =>
    onCategoryChange(selectedCategory === cat ? "All" : cat);

  return (
    <Wrapper>
      <ArrowButton left onClick={() => scrollBy(-150)} aria-label="Scroll left">
        <FiChevronLeft />
      </ArrowButton>

      <ChipContainer
        ref={containerRef}
        onWheel={handleWheel}
        onMouseDown={handleDrag}
      >
        {leadingChips.map((chip) => (
          <Chip
            key={chip.key}
            selected={chip.selected}
            onClick={chip.onClick}
          >
            {chip.label}
            {chip.selected && (
              <CloseIcon>
                <FiX size={12} />
              </CloseIcon>
            )}
          </Chip>
        ))}
        {filtered.map((cat) => (
          <Chip
            key={cat}
            selected={selectedCategory === cat}
            onClick={() => toggle(cat)}
          >
            {cat}
            {selectedCategory === cat && (
              <CloseIcon>
                <FiX size={12} />
              </CloseIcon>
            )}
          </Chip>
        ))}
      </ChipContainer>

      <ArrowButton onClick={() => scrollBy(150)} aria-label="Scroll right">
        <FiChevronRight />
      </ArrowButton>
    </Wrapper>
  );
};

export default CategoryChips;


